import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Availability } from '../../core/model/availability';
import type { Restaurant, Review } from '../../core/model/restaurant';
import { addressSingleLine, hasCoordinates, hasRating } from '../../core/model/restaurant';
import { Formatters } from '../../core/util/formatters';
import { PhoneNormalizer } from '../../core/util/phoneNormalizer';
import { dayOfWeek, today } from '../../core/util/datetime';
import { S } from '../../i18n/strings';
import { useContainer } from '../../state/ContainerContext';
import { useCriteria } from '../../state/CriteriaContext';
import { availabilityStateOf, type AvailabilityState } from '../../state/availabilityState';
import { UiStates } from '../../state/uiState';
import { useAsync } from '../../state/useAsync';
import type { Page } from '../../data/repository/repositories';
import { AvailabilityPanel } from '../components/AvailabilityPanel';
import { SearchCriteriaBar } from '../components/SearchCriteriaBar';
import { SkeletonBlock, StateContainer } from '../components/StateContainer';
import { Photo } from '../components/Photo';
import { MapIcon, MenuIcon, PhoneIcon, StarIcon } from '../components/icons';

/**
 * Detalle del restaurante.
 *
 * Cada sección se compone únicamente si su dato existe: sin descripción no hay "Acerca de", sin
 * teléfono no hay fila "Llamar" (ausente, no deshabilitada) y sin horarios se dice justo eso,
 * en lugar de mostrar una tabla vacía.
 */
export function RestaurantDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { restaurants } = useContainer();
  const { criteria, setDate, setTime, setPartySize } = useCriteria();

  const { state, reload } = useAsync(() => restaurants.getRestaurant(id), [restaurants, id]);

  const availability = useAsync<Availability, AvailabilityState>(
    () => restaurants.getAvailability(id, criteria.date, criteria.time, criteria.partySize),
    [restaurants, id, criteria.date, criteria.time, criteria.partySize],
    (data) => UiStates.success(availabilityStateOf(data)),
  );

  const availabilityState: AvailabilityState =
    availability.state.status === 'SUCCESS'
      ? availability.state.data
      : availability.state.status === 'ERROR'
        ? { status: 'ERROR', error: availability.state.error }
        : { status: 'LOADING' };

  return (
    <StateContainer state={state} loading={<SkeletonBlock height={280} />} onRetry={reload}>
      {(restaurant) => (
        <div className="stack stack--loose">
          <Gallery restaurant={restaurant} />

          <section className="stack stack--tight">
            <h1>{restaurant.name}</h1>
            <div className="meta-row">
              {hasRating(restaurant) && (
                <span className="rating">
                  <StarIcon size={15} className="rating__star" />
                  {Formatters.rating(restaurant.rating, restaurant.reviewCount)}
                  <span className="muted">{S.restaurantReviews(restaurant.reviewCount)}</span>
                </span>
              )}
              {restaurant.cuisine && <span>{restaurant.cuisine}</span>}
              {Formatters.priceLevel(restaurant.priceLevel) && (
                <span>{Formatters.priceLevel(restaurant.priceLevel)}</span>
              )}
              {Formatters.distance(restaurant.distanceMeters) && (
                <span>{S.restaurantDistance(Formatters.distance(restaurant.distanceMeters)!)}</span>
              )}
            </div>
            {/* `null` es "no lo sabemos" y no se pinta; solo `true`/`false` producen chip. */}
            {restaurant.isCurrentlyOpen !== null && (
              <span
                className={`chip chip--static ${restaurant.isCurrentlyOpen ? 'chip--open' : 'chip--closed'}`}
                style={{ width: 'fit-content' }}
              >
                {restaurant.isCurrentlyOpen ? S.restaurantOpenNow : S.restaurantClosedNow}
              </span>
            )}
            {restaurant.promotion && (
              <span className="chip chip--static chip--promo" style={{ width: 'fit-content' }}>
                {restaurant.promotion.label}
                {restaurant.promotion.description && ` · ${restaurant.promotion.description}`}
              </span>
            )}
          </section>

          <section className="section">
            <h2>{S.availabilityTitle}</h2>
            <SearchCriteriaBar />
            <AvailabilityPanel
              state={availabilityState}
              onSelectSlot={() => navigate(`/reservar/${restaurant.id}`)}
              onSelectAlternative={(alternative) => {
                // La alternativa no solo navega: reescribe los criterios, para que la pantalla
                // siguiente muestre exactamente lo que el usuario acaba de aceptar.
                setDate(alternative.date);
                setTime(alternative.time);
                setPartySize(alternative.partySize);
              }}
              onRetry={availability.reload}
            />
          </section>

          {restaurant.description && (
            <section className="section">
              <h2>{S.restaurantAbout}</h2>
              <p>{restaurant.description}</p>
            </section>
          )}

          <OpeningHoursSection restaurant={restaurant} />

          {restaurant.amenities.length > 0 && (
            <section className="section">
              <h2>{S.restaurantAmenities}</h2>
              <div className="row">
                {restaurant.amenities.map((amenity) => (
                  <span key={amenity} className="chip chip--static">
                    {amenity}
                  </span>
                ))}
              </div>
            </section>
          )}

          <ContactSection restaurant={restaurant} />

          <ReviewsSection restaurantId={restaurant.id} />

          <div className="detail-cta">
            <button
              type="button"
              className="btn btn--block"
              onClick={() => navigate(`/reservar/${restaurant.id}`)}
            >
              {S.restaurantBook}
            </button>
          </div>
        </div>
      )}
    </StateContainer>
  );
}

function Gallery({ restaurant }: { restaurant: Restaurant }) {
  const [index, setIndex] = useState(0);

  return (
    <div>
      <div className="hero">
        <Photo
          className="hero__image"
          src={restaurant.photos[index] ?? null}
          alt={S.restaurantPhoto(restaurant.name)}
          eager
          style={{ aspectRatio: '16 / 9' }}
        />
      </div>
      {restaurant.photos.length > 1 && (
        <div className="hero__thumbs">
          {restaurant.photos.map((photo, i) => (
            <button
              key={photo}
              type="button"
              className={`hero__thumb ${i === index ? 'hero__thumb--active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`${S.restaurantPhoto(restaurant.name)} ${i + 1}`}
              aria-current={i === index}
            >
              <Photo src={photo} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OpeningHoursSection({ restaurant }: { restaurant: Restaurant }) {
  const currentDay = dayOfWeek(today());

  return (
    <section className="section">
      <h2>{S.restaurantHours}</h2>
      {restaurant.openingHours.length === 0 ? (
        // Se dice que no se sabe, en lugar de dejar un bloque vacío que parece un fallo.
        <p className="muted">{S.restaurantHoursUnavailable}</p>
      ) : (
        <div className="hours-table">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => {
            const ranges = restaurant.openingHours.filter((h) => h.dayOfWeek === day);
            return (
              <div key={day} style={{ display: 'contents' }}>
                <span
                  className={`hours-table__day ${day === currentDay ? 'hours-table__day--today' : ''}`}
                >
                  {Formatters.weekdayName(day)}
                </span>
                <span>
                  {ranges.length === 0
                    ? S.restaurantClosedToday
                    : ranges.map((r) => `${r.opensAt}–${r.closesAt}`).join(', ')}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ContactSection({ restaurant }: { restaurant: Restaurant }) {
  const address = addressSingleLine(restaurant.address);
  const hasAnything = restaurant.phone || restaurant.menuUrl || address;
  if (!hasAnything) return null;

  const mapsUrl = hasCoordinates(restaurant.address)
    ? `https://www.openstreetmap.org/?mlat=${restaurant.address!.latitude}&mlon=${restaurant.address!.longitude}#map=17/${restaurant.address!.latitude}/${restaurant.address!.longitude}`
    : null;

  return (
    <section className="section">
      <h2>{S.restaurantContact}</h2>
      {address && <p>{address}</p>}
      <div className="stack stack--tight">
        {/* Sin teléfono la fila no existe: un botón deshabilitado solo genera intentos fallidos. */}
        {restaurant.phone && (
          <a className="info-row" href={`tel:+52${PhoneNormalizer.normalize(restaurant.phone) ?? restaurant.phone}`}>
            <PhoneIcon size={18} /> {S.restaurantCall} · {PhoneNormalizer.format(restaurant.phone)}
          </a>
        )}
        {restaurant.menuUrl && (
          <a className="info-row" href={restaurant.menuUrl} target="_blank" rel="noreferrer noopener">
            <MenuIcon size={18} /> {S.restaurantMenu}
          </a>
        )}
        {mapsUrl && (
          <a className="info-row" href={mapsUrl} target="_blank" rel="noreferrer noopener">
            <MapIcon size={18} /> {S.restaurantMap}
          </a>
        )}
      </div>
    </section>
  );
}

function ReviewsSection({ restaurantId }: { restaurantId: string }) {
  const { restaurants } = useContainer();
  const { state } = useAsync<Page<Review>, readonly Review[]>(
    () => restaurants.getReviews(restaurantId),
    [restaurants, restaurantId],
    (page) => UiStates.fromList(page.items),
  );

  return (
    <section className="section">
      <h2>{S.restaurantReviewsSection}</h2>
      <StateContainer
        state={state}
        loading={<SkeletonBlock height={80} />}
        empty={<p className="muted">{S.restaurantNoReviews}</p>}
      >
        {(reviews) => (
          <div className="stack stack--tight">
            {reviews.map((review) => (
              <article key={review.id} className="card" style={{ padding: 14 }}>
                <div className="row">
                  <strong>{review.authorName}</strong>
                  <span className="rating">
                    <StarIcon size={13} className="rating__star" />
                    {review.rating.toFixed(1)}
                  </span>
                  {review.date && <span className="muted">{Formatters.longDate(review.date)}</span>}
                </div>
                {/* Una reseña sin comentario muestra solo su calificación, no un hueco. */}
                {review.comment && <p style={{ marginTop: 6 }}>{review.comment}</p>}
              </article>
            ))}
          </div>
        )}
      </StateContainer>
    </section>
  );
}
